import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// CRITICAL: Disable credentials mode to avoid CORS conflict with wildcard origin
axios.defaults.withCredentials = false;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Token management
const getAccessToken = () => localStorage.getItem('access_token');
const getRefreshToken = () => localStorage.getItem('refresh_token');
const setTokens = (access, refresh) => {
  if (access) localStorage.setItem('access_token', access);
  if (refresh) localStorage.setItem('refresh_token', refresh);
};
const clearTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

// Setup axios defaults - add auth header to all requests
axios.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for auto-refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url?.includes('/auth/refresh') ||
          originalRequest.url?.includes('/auth/login') ||
          originalRequest.url?.includes('/auth/register')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          originalRequest.headers.Authorization = `Bearer ${getAccessToken()}`;
          return axios(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshTkn = getRefreshToken();
        if (!refreshTkn) throw new Error('No refresh token');
        
        const { data } = await axios.post(`${API}/api/auth/refresh`, { refresh_token: refreshTkn });
        if (data.access_token) {
          setTokens(data.access_token, null);
        }
        processQueue(null);
        originalRequest.headers.Authorization = `Bearer ${getAccessToken()}`;
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        clearTokens();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  const checkAuth = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      // Try refresh token
      const refreshTkn = getRefreshToken();
      if (refreshTkn) {
        try {
          const { data } = await axios.post(`${API}/api/auth/refresh`, { refresh_token: refreshTkn });
          if (data.access_token) setTokens(data.access_token, null);
        } catch {
          clearTokens();
          setUser(false);
          setLoading(false);
          return;
        }
      } else {
        setUser(false);
        setLoading(false);
        return;
      }
    }
    
    try {
      const { data } = await axios.get(`${API}/api/auth/me`);
      setUser(data);
    } catch {
      clearTokens();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh token every 45 minutes (token expires at 60)
  useEffect(() => {
    if (user) {
      refreshTimerRef.current = setInterval(async () => {
        try {
          const refreshTkn = getRefreshToken();
          if (refreshTkn) {
            const { data } = await axios.post(`${API}/api/auth/refresh`, { refresh_token: refreshTkn });
            if (data.access_token) setTokens(data.access_token, null);
          }
        } catch {
          // Will be caught by interceptor on next request
        }
      }, 45 * 60 * 1000);
    }
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [user]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      if (data.access_token) setTokens(data.access_token, data.refresh_token);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name, extras = {}) => {
    try {
      const payload = { email, password, name, ...extras };
      const { data } = await axios.post(`${API}/api/auth/register`, payload);
      if (data.access_token) setTokens(data.access_token, data.refresh_token);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {});
    } catch {
      // Ignore errors
    }
    clearTokens();
    setUser(false);
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  const isSubscribed = user && (user.subscription_status === 'active' || user.subscription_status === 'cancelled');
  const isAdmin = user && user.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, isSubscribed, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};
